import React, { useState, useCallback, useEffect, useRef } from 'react';
import { forIn, mapValues } from 'lodash';
import { useDispatch } from 'react-redux';
import { Icon } from '@doxyme/ui';
import { localeService, components as Core } from '../../../core';
import { getTabbableElements } from '../../../core/helpers/get-tabbable-elements';
import { SurveyStep, SurveyTab, SurveyData, SurveyStarValue, RatingStars, SurveyTabs, } from '../../constants';
import { hideSurvey, hideSurveyDelayed, submitRating } from '../../actions';
import { ButtonType, Button } from '../../../../components/button';
import { Checkbox } from '../../../../components/checkbox';
import { CallSurveyHeader } from './CallSurveyHeader';
import { CallSurveyCloseButton } from './CallSurveyCloseButton';
import './CallQualitySurvey.scss';
export const CallQualitySurvey = () => {
    const dispatch = useDispatch();
    const [isSubmitDisabled, setSubmitStatus] = useState(true);
    const [starHoverValue, setStarHoverValue] = useState(0);
    const [selectedRatingValue, setRatingValue] = useState(0);
    const [currentTab, setCurrentTab] = useState(SurveyTab.Video);
    const [currentStep, setCurrentStep] = useState(SurveyStep.Start);
    const [surveyData, setSurveyData] = useState(SurveyData);
    const [comment, setComment] = useState('');
    const [focusedStarIndex, setFocusedStarIndex] = useState(0);
    const starRefs = useRef([]);
    const tabRefs = useRef([]);
    const wrapperRef = useRef(null);
    const previouslyFocusedRef = useRef(null);
    const ratingStars = RatingStars;
    const tabs = SurveyTabs;
    const dialogTitleId = 'call-quality-survey__title';
    const tabId = (tab) => `call-quality-survey__tab--${tab}`;
    const tabPanelId = 'call-quality-survey__tabpanel';
    const resultsMapper = useCallback(() => {
        const result = [];
        forIn(surveyData, (results) => {
            forIn(mapValues(results, 'value'), (val, problem) => {
                if (val) {
                    result.push(problem);
                }
            });
        });
        return result;
    }, [surveyData]);
    const handleStarClick = (value) => {
        setRatingValue(value);
        if (value === SurveyStarValue.Great) {
            setCurrentStep(SurveyStep.Final);
            const payload = {
                rating: value,
                data: resultsMapper(),
                comment,
                isProviderFeedback: true,
            };
            dispatch(submitRating(payload));
        }
        else {
            setCurrentStep(SurveyStep.Response);
        }
    };
    const handleCommentChange = (e) => {
        setComment(e.target.value);
    };
    const back = useCallback(() => {
        setSurveyData(SurveyData);
        setCurrentStep(SurveyStep.Start);
        setCurrentTab(SurveyTab.Video);
    }, []);
    useEffect(() => {
        setSubmitStatus(resultsMapper().length === 0 && comment.length === 0);
    }, [surveyData, comment, resultsMapper]);
    const handleCheckboxClick = (id, v) => {
        setSurveyData({
            ...surveyData,
            [currentTab]: {
                ...surveyData[currentTab],
                [id]: {
                    ...surveyData[currentTab][id],
                    value: !v,
                },
            },
        });
    };
    const hide = (shouldSubmit = false) => {
        dispatch(hideSurvey());
        if (shouldSubmit) {
            const payload = {
                rating: selectedRatingValue,
                data: [],
                comment: '',
                isProviderFeedback: true,
            };
            dispatch(submitRating(payload));
        }
    };
    const isValueGreaterThan = (value) => {
        return starHoverValue >= value || selectedRatingValue >= value;
    };
    const submit = useCallback(() => {
        setCurrentStep(SurveyStep.Final);
        dispatch(hideSurveyDelayed());
        const payload = {
            rating: selectedRatingValue,
            data: resultsMapper(),
            comment,
            isProviderFeedback: true,
        };
        dispatch(submitRating(payload));
    }, [dispatch, selectedRatingValue, resultsMapper, comment]);
    const focusStarAt = (index) => {
        const targetIndex = Math.max(0, Math.min(ratingStars.length - 1, index));
        setFocusedStarIndex(targetIndex);
        starRefs.current[targetIndex]?.focus();
    };
    useEffect(() => {
        previouslyFocusedRef.current = document.activeElement;
        return () => {
            const previouslyFocused = previouslyFocusedRef.current;
            if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
                previouslyFocused.focus();
            }
        };
    }, []);
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }
        const tabbable = getTabbableElements(wrapper);
        if (tabbable.length > 0) {
            tabbable[0].focus();
        }
        else {
            wrapper.focus();
        }
    }, [currentStep]);
    const handleDialogKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            hide();
            return;
        }
        if (event.key !== 'Tab') {
            return;
        }
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }
        const tabbable = getTabbableElements(wrapper);
        if (tabbable.length === 0) {
            event.preventDefault();
            return;
        }
        const first = tabbable[0];
        const last = tabbable[tabbable.length - 1];
        const active = document.activeElement;
        if (!active || !wrapper.contains(active)) {
            event.preventDefault();
            first.focus();
            return;
        }
        if (active === wrapper) {
            event.preventDefault();
            if (event.shiftKey) {
                last.focus();
            }
            else {
                first.focus();
            }
            return;
        }
        if (event.shiftKey) {
            if (active === first) {
                event.preventDefault();
                last.focus();
            }
        }
        else if (active === last) {
            event.preventDefault();
            first.focus();
        }
    };
    const handleStarKeyDown = (event, index, value) => {
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault();
                focusStarAt(index + 1);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault();
                focusStarAt(index - 1);
                break;
            case 'Home':
                event.preventDefault();
                focusStarAt(0);
                break;
            case 'End':
                event.preventDefault();
                focusStarAt(ratingStars.length - 1);
                break;
            case ' ':
            case 'Enter':
                event.preventDefault();
                handleStarClick(value);
                break;
            default:
                break;
        }
    };
    const focusTabAt = (index) => {
        const targetIndex = (index + tabs.length) % tabs.length;
        setCurrentTab(tabs[targetIndex]);
        tabRefs.current[targetIndex]?.focus();
    };
    const handleTabKeyDown = (event, index) => {
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault();
                focusTabAt(index + 1);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault();
                focusTabAt(index - 1);
                break;
            case 'Home':
                event.preventDefault();
                focusTabAt(0);
                break;
            case 'End':
                event.preventDefault();
                focusTabAt(tabs.length - 1);
                break;
            case ' ':
            case 'Enter':
                event.preventDefault();
                setCurrentTab(tabs[index]);
                break;
            default:
                break;
        }
    };
    const renderCurrentStep = () => {
        switch (currentStep) {
            case SurveyStep.Response:
                return (<div>
            <CallSurveyHeader title="call-quality-survey.results" subtitle="call-quality-survey.select" titleId={dialogTitleId}/>
            <CallSurveyCloseButton onClick={() => hide(true)}/>
            <button className="call-quality-survey__back" onClick={back} aria-label={localeService.formatMessageById('common.go-back')}>
              <Icon className="call-quality-survey__icon" name="chevron-left" size="x-small"/>
            </button>
            <div className="call-quality-survey__tabs-wrapper">
              <ul role="tablist" aria-label={localeService.formatMessageById('call-quality-survey.tabs.label')} className="call-quality-survey__tabs">
                {tabs.map((tab, i) => {
                        const isSelected = currentTab === tab;
                        return (<li key={`${tab} + ${i}`} ref={(el) => {
                                tabRefs.current[i] = el;
                            }} role="tab" id={tabId(tab)} aria-selected={isSelected} aria-controls={tabPanelId} tabIndex={isSelected ? 0 : -1} className={[
                                'call-quality-survey__tabs__tab',
                                'call-quality-survey__tab',
                                isSelected ? 'active' : '',
                            ].join(' ')} onClick={() => setCurrentTab(tab)} onKeyDown={(event) => handleTabKeyDown(event, i)}>
                      <span>
                        {localeService.formatMessageById(`call-quality-survey.tab.${tab}`)}
                      </span>
                    </li>);
                    })}
                <li className="call-quality-survey__tabs__slider" role="presentation" aria-hidden="true"/>
              </ul>
              <div role="tabpanel" id={tabPanelId} aria-labelledby={tabId(currentTab)} tabIndex={0} className="call-quality-survey__tabs-content">
                {currentTab !== SurveyTab.Other
                        ? Object.keys(surveyData[currentTab]).map((option, i) => (<div className="call-quality-survey__tabs-content__rows" key={`${option} + ${i}`}>
                        <form name="form" role="form">
                          <Checkbox id={`active-${i}`} checked={surveyData[currentTab][option].value} label={localeService.formatMessageById(surveyData[currentTab][option].title)} onChange={() => handleCheckboxClick(option, surveyData[currentTab][option].value)}/>
                        </form>
                      </div>))
                        : null}
                {currentTab === SurveyTab.Other ? (<>
                    <textarea className="call-quality-survey__comment" value={comment} onChange={handleCommentChange} placeholder={localeService.formatMessageById('call-quality-survey.comment.placeholder')}/>
                    <p>
                      {localeService.formatMessageById('call-quality-survey.comment.PII-warning')}
                    </p>
                  </>) : null}
              </div>
              <div className="call-quality-survey__footer">
                <Button type={ButtonType.PRIMARY} onClick={submit} disabled={isSubmitDisabled}>
                  {localeService.formatMessageById('apps.action.submit')}
                </Button>
              </div>
            </div>
          </div>);
            case SurveyStep.Final:
                return (<div>
            <CallSurveyHeader title="call-quality-survey.thanks" subtitle="call-quality-survey.good-day" titleId={dialogTitleId}/>
            <CallSurveyCloseButton onClick={hide}/>
            <div className="call-quality-survey-final__footer">
              <Button type={ButtonType.PRIMARY} onClick={hide}>
                {localeService.formatMessageById('apps.action.dismiss')}
              </Button>
            </div>
          </div>);
            default:
                return (<div>
            <CallSurveyHeader title="call-quality-survey.title" subtitle="call-quality-survey.subtitle" titleId={dialogTitleId}/>
            <CallSurveyCloseButton onClick={hide}/>
            <div role="radiogroup" className="call-quality-survey__rating" onMouseLeave={() => setStarHoverValue(0)}>
              {ratingStars.map((star, index) => (<div role="radio" tabIndex={index === focusedStarIndex ? 0 : -1} aria-checked={selectedRatingValue === star.value} aria-label={localeService.formatMessageById(star.label)} key={`star${star.value}`} ref={(el) => {
                            starRefs.current[index] = el;
                        }} className={[
                            'call-quality-survey__rating-item',
                            isValueGreaterThan(star.value)
                                ? 'call-quality-survey__rating-item--active'
                                : '',
                        ].join(' ')} onMouseEnter={() => setStarHoverValue(star.value)} onFocus={() => setFocusedStarIndex(index)} onKeyDown={(event) => handleStarKeyDown(event, index, star.value)} onClick={() => handleStarClick(star.value)}>
                  <Icon className="call-quality-survey__rating_ico" name="star"/>
                  <span className="call-quality-survey__rating_label">
                    <Core.RawHTML>
                      {localeService.formatMessageById(star.label)}
                    </Core.RawHTML>
                  </span>
                </div>))}
            </div>
          </div>);
        }
    };
    return (<div ref={wrapperRef} role="dialog" aria-modal="true" aria-labelledby={dialogTitleId} tabIndex={-1} onKeyDown={handleDialogKeyDown} className={[
            'call-quality-survey__wrapper',
            currentStep === SurveyStep.Response ? 'full-height' : '',
        ].join(' ')}>
      {renderCurrentStep()}
    </div>);
};
